import dayjs from 'dayjs'
import ThermalPrinterBase from '../../services/thermalPrinter/ThermalPrinterBase'
import { useFormatNumber } from '~/composables/useUtils'
import { isMoneyMasked, MASKED_MONEY } from '~/composables/useDashboardMoneyMask'
import {
  buildProductSummaryByTypeFromSumProductType,
  mapSummaryByTypeToDetailRows,
  mapSummaryByTypeToRows,
} from '~/utils/printerProductSummary'
import { buildProductSoldItemRows } from '~/utils/productSoldThermalRows'

type ProductSummaryLine = {
  label: string
  value: number
}

type ProductSummaryDetailSub = {
  name: string
  qty: number
  amount: number
}

type ProductSummaryDetail = {
  typeName: string
  productName: string
  total: number
  subs: ProductSummaryDetailSub[]
}

type ProductSummaryPayload = {
  storeName: string
  fromText: string
  toText: string
  reportName?: string
  printedAtText?: string
  topCommentText?: string
  totalAmount: number
  byType?: ProductSummaryLine[]
  details?: ProductSummaryDetail[]
  sumProductType?: Array<{ name: string; groupByType: Record<string, any> }>
}

export default class ThermalPrintProductSummary extends ThermalPrinterBase {
  printProductSummary(payload: ProductSummaryPayload) {
    this.print_data = this.withPayload(payload)
    return this
  }

  withPayload(payload: ProductSummaryPayload) {
    const now = dayjs()
    const topComment = payload.topCommentText || `****** พิมพ์ย้อนหลัง ${now.format('HH:mm DD/MMM/YY')}*****`
    const reportName = payload.reportName || 'Product Sold'
    const printedAtText = payload.printedAtText || now.format('DD MMMM YYYY HH:mm')

    const summaryByType = buildProductSummaryByTypeFromSumProductType(payload.sumProductType || [], {
      dropZeroCatalogs: false,
      dropZeroSubItems: false,
    })

    const byTypeFromSummary = mapSummaryByTypeToRows(summaryByType)
    const detailFromSummary = mapSummaryByTypeToDetailRows(summaryByType)

    const byTypeRows = byTypeFromSummary.length ? byTypeFromSummary : (payload.byType || [])
    const detailRows = detailFromSummary.length ? detailFromSummary : (payload.details || [])

    const commands: any[] = [
      { name: 'PrintComment', value: topComment },
      { name: 'LineFeed', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: payload.storeName || '-' },
      { name: 'LineFeed', value: true },
      { name: 'From', value: payload.fromText || '-' },
      { name: 'To', value: payload.toText || '-' },
      { name: 'Printed at', value: printedAtText },
      { name: 'Report', value: reportName },
      { name: 'LineFeed', value: true },
      { name: 'PrintLine', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: 'Products by type' },
      { name: 'LineFeed', value: true },
      ...byTypeRows.map((line) => ({
        name: line.label,
        value: this.amount(line.value),
      })),
      { name: 'LineFeed', value: true },
      { name: 'Total', value: `*** ${this.amount(payload.totalAmount)}` },
      { name: 'LineFeed', value: true },
      { name: 'PrintComment', value: 'ไม่รวมส่วนลดท้ายบิล/Service/VAT' },
      { name: 'LineFeed', value: true },
      { name: 'PrintLine', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: 'Detail' },
      { name: 'LineFeed', value: true },
    ]

    detailRows.forEach((detail: any) => {
      commands.push({
        name: 'PrintComment',
        value: `*** ${detail.typeName} : ${detail.productName} :${this.amount(detail.total)}`,
      })

      const items = Array.isArray(detail.items) && detail.items.length
        ? detail.items
        : (detail.subs || []).map((s: any) => ({
            itemName: s.itemName || s.name,
            subItems: [{ subName: s.subName || '', qty: s.qty, amount: s.amount }],
          }))

      for (const item of items) {
        const rows = buildProductSoldItemRows(item, (n) => this.amount(n))
        for (const row of rows) commands.push(row)
      }

      commands.push({ name: 'LineFeed', value: true })
      commands.push({ name: 'LineFeed', value: true })
    })

    commands.push({ name: 'PrintComment', value: topComment })
    commands.push({ name: 'LineFeed', value: true })
    commands.push({ name: 'LineFeed', value: true })

    return commands
  }

  // Hide-cashier: mask every amount on the Products Sold report for HideCashier
  // PINs (matches the on-screen dashboard + close-shop report mask).
  private amount(value: number) {
    if (isMoneyMasked()) return `   ${MASKED_MONEY}`
    return `   ${useFormatNumber(Number(value || 0), 2, true)}`
  }
}
